import * as React from 'react';
import { EditState, EditProps, BaseRouteProps } from '../../interfaces';
import { Survey, Question, Organization } from '../../models';
import { SurveyDataService, QuestionDataService, OrganizationDataService, ImportDataService } from '../../services';
import { NavLink } from 'react-router-dom';
import { TopNavMenu } from '../navigation/TopNavMenu';
import { SurveyForm } from './SurveyForm';
import { DataGrid, LoadingIndicator } from '../../components';
import { UserStore, FilterStore } from '../../stores';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

interface SurveyEditProps extends BaseRouteProps<EditProps> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

interface SurveyEditState extends EditState<Survey> {
    surveyIsPublished?: boolean;
    loading: boolean;
    orgImportId: number;
    organizations: Organization[];
    open: boolean;
}

@inject('UserStore', 'FilterStore') @observer
export class SurveyEdit extends React.Component<SurveyEditProps, SurveyEditState> {
    private surveyDataService = new SurveyDataService(this.props.notificationService);
    private questionDataService = new QuestionDataService(this.props.notificationService);
    private organizationDataService = new OrganizationDataService(this.props.notificationService);
    private importDataService = new ImportDataService(this.props.notificationService);

    @computed get userLanguage() {
        return this.props.UserStore!.userInfo.language;
    }

    @computed get isSuperAdmin() {
        return this.props.UserStore!.userRoleIn(['superAdmin']);
    }

    constructor(props: SurveyEditProps) {
        super(props);

        this.state = {
            originalEntity: undefined,
            loading: false,
            orgImportId: 0,
            organizations: [],
            open: false
        };

        this.save = this.save.bind(this);
        this.createCopy = this.createCopy.bind(this);
        this.deleteQuestionBinder = this.deleteQuestionBinder.bind(this);
        this.handleOrderBlur = this.handleOrderBlur.bind(this);
        this.updateQuestionSortOrder = this.updateQuestionSortOrder.bind(this);
    }

    public componentDidMount() {
        const id = this.props.match.params.id;

        this.organizationDataService.getAllByUser().then((organizations: Organization[]) => {
            this.setState({
                organizations: organizations
            });
        });

        if (id) {
            this.surveyDataService.get(id).then((survey: Survey) => {
                survey.questions = survey.questions || [];
                this.setState({ originalEntity: survey });

                if (survey.surveyVersions && survey.surveyVersions.some(x => x.published != null)) {
                    this.setState({ surveyIsPublished: true });
                } else {
                    this.setState({ surveyIsPublished: false });
                }
            }).catch((response) => {
                console.log(response);
                this.props.history.push({
                    pathname: '/error',
                    state: {
                        code: response.status,
                        entityType: 'survey'
                    }
                });
            });
        }
    }

    private updateTemp(event: any) {
        const questionId = Number(event.target.dataset.questionId);
        let newSortValue = Number(event.target.value);

        let questions = this.state.originalEntity!.questions;
        let questionIndex = questions.find(x => x.id === questionId);

        if (questionIndex) {
            questionIndex.tempSort = newSortValue;

            this.setState(
                {
                    originalEntity: Object.assign(
                        {},
                        this.state.originalEntity,
                        { questions: questions }
                    )
                }
            );
        }
    }

    private handleOrderBlur(event: any): any {
        // we need to convert these to numbers because pulling it off the data-question-id property gives us a string
        const questionId = Number(event.target.dataset.questionId);
        let newSortValue = Number(event.target.value);

        let questions = this.state.originalEntity!.questions;
        let questionIndex = questions.find(x => x.id === questionId);

        if (questionIndex) {
            questionIndex.sort = newSortValue;

            this.setState(
                {
                    originalEntity: Object.assign(
                        {},
                        this.state.originalEntity,
                        { questions: questions }
                    )
                }
            );
        }
    }

    private StatementsDataGridTrueFalse() {
        const { originalEntity, surveyIsPublished } = this.state;
        if (!originalEntity) {
            return <div />;
        }

        const listData = originalEntity.questions
            .filter((q: Question) => {
                return q.questionType === 'binary';
            })
            .sort((q1: Question, q2: Question) => {
                if (q1.sort > q2.sort) { return 1; }
                if (q1.sort < q2.sort) { return -1; }
                return 0;
            })
            .map((entity) => {
                return {
                    'Order': { 'sort': entity.sort, 'id': entity.id, 'tempSort': entity.tempSort || entity.sort },
                    'Statement': this.userLanguage ? Question.Text(entity, this.userLanguage) : '',
                    'ActionKey': entity.id
                };
            });

        const orderColumn = ({ value }: any) => {
            const tempSort = value.get('tempSort');
            const id = value.get('id');

            return (
                <input id={id} key={`question-sort-${id}`} type="number" data-question-id={id} className="form-control" min="1" value={tempSort} onChange={(e) => this.updateTemp(e)} onBlur={this.handleOrderBlur} />
            );
        };

        const orderColumnSort = (data: any, column: any, sortAscending = true) => {
            return data.sort(
                (firstValue: any, secondValue: any) => {
                    const firstValueSort = firstValue.get(column).get('sort');
                    const secondValueSort = secondValue.get(column).get('sort');

                    if (sortAscending) {
                        return firstValueSort - secondValueSort;
                    } else {
                        return secondValueSort - firstValueSort;
                    }
                });
        };

        let actionsColumn;
        if (surveyIsPublished) {
            actionsColumn = ({ value }: any) => {
                return (
                    <NavLink className="btn btn-default" to={`/question/${value}`}>View</NavLink>
                );
            };
        } else {
            actionsColumn = ({ value }: any) => {
                return (
                    <span>
                        <NavLink className="btn btn-default" to={`/question/${value}`}>View</NavLink>
                        &nbsp;&nbsp;
                        <button className="btn btn-danger" onClick={this.deleteQuestionBinder(value)}>Remove</button>
                    </span>
                );
            };
        }

        const columnDefs = [
            { id: 'Order', title: '#', width: '100px', customComponent: orderColumn, sortMethod: orderColumnSort, sortable: false },
            { id: 'Statement', sortable: false },
            { id: 'ActionKey', title: `Actions`, width: '160px', customComponent: actionsColumn, sortable: false }
        ];

        const defaultSort = [
            { id: 'Order', sortAscending: true }
        ];

        // we need a key here to update column defs when the survey is published. 
        // Griddle only binds columns in the constructor, so they can't be updated in a preexisting component
        return <DataGrid key={`grid-key-${surveyIsPublished}`} entities={listData} columns={columnDefs} pageSize={listData.length} sortProperties={defaultSort} />;
    }

    private StatementsDataGridText() {
        const { originalEntity, surveyIsPublished } = this.state;
        if (!originalEntity) {
            return <div />;
        }

        const listData = originalEntity.questions
            .filter((q: Question) => {
                return q.questionType === 'text' || q.questionType === 'freeText';
            })
            .sort((q1: Question, q2: Question) => {
                if (q1.sort > q2.sort) { return 1; }
                if (q1.sort < q2.sort) { return -1; }
                return 0;
            })
            .map((entity) => {
                return {
                    'Order': { 'sort': entity.sort, 'id': entity.id, 'tempSort': entity.tempSort || entity.sort },
                    'Statement': this.userLanguage ? Question.Text(entity, this.userLanguage) : '',
                    'ActionKey': entity.id
                };
            });

        const orderColumn = ({ value }: any) => {
            const tempSort = value.get('tempSort');
            const id = value.get('id');

            return (
                <input id={id} key={`question-sort-${id}`} type="number" data-question-id={id} className="form-control" min="1" value={tempSort} onChange={(e) => this.updateTemp(e)} onBlur={this.handleOrderBlur} />
            );
        };

        const orderColumnSort = (data: any, column: any, sortAscending = true) => {
            return data.sort(
                (firstValue: any, secondValue: any) => {
                    const firstValueSort = firstValue.get(column).get('sort');
                    const secondValueSort = secondValue.get(column).get('sort');

                    if (sortAscending) {
                        return firstValueSort - secondValueSort;
                    } else {
                        return secondValueSort - firstValueSort;
                    }
                });
        };

        let actionsColumn;
        if (surveyIsPublished) {
            actionsColumn = ({ value }: any) => {
                return (
                    <NavLink className="btn btn-default" to={`/question/${value}`}>View</NavLink>
                );
            };
        } else {
            actionsColumn = ({ value }: any) => {
                return (
                    <span>
                        <NavLink className="btn btn-default" to={`/question/${value}`}>View</NavLink>
                        &nbsp;&nbsp;
                        <button className="btn btn-danger" onClick={this.deleteQuestionBinder(value)}>Remove</button>
                    </span>
                );
            };
        }

        const columnDefs = [
            { id: 'Order', title: '#', width: '100px', customComponent: orderColumn, sortMethod: orderColumnSort, sortable: false },
            { id: 'Statement', sortable: false },
            { id: 'ActionKey', title: `Actions`, width: '160px', customComponent: actionsColumn, sortable: false }
        ];

        const defaultSort = [
            { id: 'Order', sortAscending: true }
        ];

        // we need a key here to update column defs when the survey is published. 
        // Griddle only binds columns in the constructor, so they can't be updated in a preexisting component
        return <DataGrid key={`grid-key-${surveyIsPublished}`} entities={listData} columns={columnDefs} pageSize={listData.length} sortProperties={defaultSort} />;
    }

    private StatementsDataGridLikert() {
        const { originalEntity, surveyIsPublished } = this.state;
        if (!originalEntity) {
            return <div />;
        }

        const listData = originalEntity.questions
            .filter((q: Question) => {
                return q.questionType === 'scale';
            })
            .sort((q1: Question, q2: Question) => {
                if (q1.sort > q2.sort) { return 1; }
                if (q1.sort < q2.sort) { return -1; }
                return 0;
            })
            .map((entity) => {
                return {
                    'Order': { 'sort': entity.sort, 'id': entity.id, 'tempSort': entity.tempSort || entity.sort },
                    'Statement': this.userLanguage ? Question.Text(entity, this.userLanguage) : '',
                    'ActionKey': entity.id
                };
            });

        const orderColumn = ({ value }: any) => {
            const tempSort = value.get('tempSort');
            const id = value.get('id');

            return (
                <input id={id} key={`question-sort-${id}`} type="number" data-question-id={id} className="form-control" min="1" value={tempSort} onChange={(e) => this.updateTemp(e)} onBlur={this.handleOrderBlur} />
            );
        };

        const orderColumnSort = (data: any, column: any, sortAscending = true) => {
            return data.sort(
                (firstValue: any, secondValue: any) => {
                    const firstValueSort = firstValue.get(column).get('sort');
                    const secondValueSort = secondValue.get(column).get('sort');

                    if (sortAscending) {
                        return firstValueSort - secondValueSort;
                    } else {
                        return secondValueSort - firstValueSort;
                    }
                });
        };

        let actionsColumn;
        if (surveyIsPublished) {
            actionsColumn = ({ value }: any) => {
                return (
                    <NavLink className="btn btn-default" to={`/question/${value}`}>View</NavLink>
                );
            };
        } else {
            actionsColumn = ({ value }: any) => {
                return (
                    <span>
                        <NavLink className="btn btn-default" to={`/question/${value}`}>View</NavLink>
                        &nbsp;&nbsp;
                        <button className="btn btn-danger" onClick={this.deleteQuestionBinder(value)}>Remove</button>
                    </span>
                );
            };
        }

        const columnDefs = [
            { id: 'Order', title: '#', width: '100px', customComponent: orderColumn, sortMethod: orderColumnSort, sortable: false },
            { id: 'Statement', sortable: false },
            { id: 'ActionKey', title: `Actions`, width: '160px', customComponent: actionsColumn, sortable: false }
        ];

        const defaultSort = [
            { id: 'Order', sortAscending: true }
        ];

        // we need a key here to update column defs when the survey is published. 
        // Griddle only binds columns in the constructor, so they can't be updated in a preexisting component
        return <DataGrid key={`grid-key-${surveyIsPublished}`} entities={listData} columns={columnDefs} pageSize={listData.length} sortProperties={defaultSort} />;
    }

    private updateQuestionSortOrder() {
        const { id, questions } = this.state.originalEntity!;

        return this.surveyDataService.updateQuestionSortOrder(id, questions);
    }

    private findDuplicateSortOrders(questions: Question[]) {
        const counts = questions.reduce((a, b) => Object.assign(a, { [b.sort]: (a[b.sort] || 0) + 1 }), {});

        return Object.keys(counts).filter((a) => counts[a] > 1);
    }

    private findDuplicateLikertSortOrders() {
        const { questions } = this.state.originalEntity!;
        const likertQuestions = questions.filter(x => x.questionType.toLowerCase() === 'scale');

        return this.findDuplicateSortOrders(likertQuestions);
    }

    private findDuplicateBinarySortOrders() {
        const { questions } = this.state.originalEntity!;
        const binaryQuestions = questions.filter(x => x.questionType.toLowerCase() === 'binary');

        return this.findDuplicateSortOrders(binaryQuestions);
    }

    private delete() {
        if (this.state.originalEntity && confirm('Are you sure you want to delete this survey?')) {
            this.surveyDataService.delete(this.state.originalEntity).then(() => {
                this.props.history.push('/survey');
            });
        }
    }
    
    private ActionsButton() {
        return (
            <div className="btn-group dropdown">
                <button type="button" className="btn btn-primary"  onClick={(e) => this.setState({open: !this.state.open })}>
                    <span>Actions </span>
                    <span className="caret" />
                </button>
                <ul className={`action-menu dropdown-menu ${this.state.open ? 'open' : ''}`}>
                    <li className="options-header">Survey Options</li>

                    <li className="options-entry" onClick={(e) => this.createCopy()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-delete" /> <span> Clone Survey</span>
                        </div>
                        <div className="options-entry-desc">
                            This will create a copy of the survey.
                        </div>
                    </li>

                    {this.isSuperAdmin && <li className="options-header">Survey Maintenance</li>}
                    {this.isSuperAdmin && <li className="options-entry" onClick={(e) => this.delete()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-delete" /> <span style={{color: 'red'}}> Delete Survey</span>
                        </div>
                        <div className="options-entry-desc">
                            This will soft delete the survey from the system.
                        </div>
                    </li>}

                </ul>
        </div>);
    }

    public render() {
        if (!this.state.originalEntity || this.state.loading) {
            return <LoadingIndicator />;
        }

        const listData = this.state.originalEntity.surveyVersions.sort((a, b) => a.version - b.version).map(entity => {
            return {
                'Name': '#' + entity.version + (entity.nickname ? (' - ' + entity.nickname + ' ') : ' ') + (entity.published == null ? '*' : '') + (entity.active ? '(live)' : ''),
                'ActionKey': entity.id
            };
        });

        const actionsColumn = ({ value }: any) => {
            return (
                <span>
                    <NavLink className="btn btn-default" to={`/surveyversion/${value}`}>View</NavLink>
                </span>
            );
        };

        const columnDefs = [
            { id: 'Name' },
            { id: 'ActionKey', title: 'Actions', width: '88px', customComponent: actionsColumn, sortable: false }
        ];

        const disabledTooltip = (
            <Tooltip id="disabled-tooltip">
                This survey has been published and can no longer have statements added
            </Tooltip>
        );

        const duplicateScaleSorts = this.findDuplicateLikertSortOrders();
        const duplicateBinarySorts = this.findDuplicateBinarySortOrders();

        const duplicateSortOrderTooltip = (
            <Tooltip id="duplicate-sort-tooltip">
                {duplicateBinarySorts.length > 0 &&
                    <div>Duplicate true/false order values were found for {`${duplicateBinarySorts.join(', ')}`}</div>
                }
                {(duplicateBinarySorts.length > 0 && duplicateScaleSorts.length > 0) &&
                    <br />
                }
                {duplicateScaleSorts.length > 0 &&
                    <div>Duplicate likert order values were found for {`${duplicateScaleSorts.join(', ')}`}</div>
                }
            </Tooltip>
        );

        const canUpdateSorts = duplicateScaleSorts.length === 0 && duplicateBinarySorts.length === 0;

        return (
            <div>
                <TopNavMenu open={true} />

                <div className="content-header">
                    <div className="content-container">
                        <div className="row">
                            <div className="col-sm-10 col-md-8 col-xs-12">       
                                <div className="title">
                                    <NavLink to={'/survey'}>
                                        <span className="glyphicon glyphicon-triangle-left" />
                                    </NavLink>
                                    &nbsp; 
                                    Edit Survey
                                </div>
                                <i>This is the main entry content point for this survey.</i>
                            </div>
                            <div className="col-sm-2 col-md-4 text-right">
                                {this.ActionsButton()}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="content-body">
                    <div className="content-container">
                        <SurveyForm defaultValues={this.state.originalEntity} submit={(e: any) => this.save(e)} />

                        <hr />

                        <h3>Versions</h3>
                        <i>These are all of the versions of this Survey. Each version contains the Scoring Logic based on Characteristic Groups, Characteristics, Raw Scores, and Styles.</i>
                        <DataGrid entities={listData} columns={columnDefs} />

                        <hr />

                        <h3>
                            Statements
                            <span className="pull-right">
                                {this.state.originalEntity!.questions.length >= 2 &&
                                    <span>
                                        {canUpdateSorts &&
                                            <button className="btn btn-default" onClick={this.updateQuestionSortOrder}>Update Sort Values</button>
                                        }
                                        {!canUpdateSorts &&
                                            <OverlayTrigger animation={''} delay={0} placement="bottom" overlay={duplicateSortOrderTooltip}>
                                                <span style={{ cursor: 'not-allowed' }}>
                                                    <button className="btn btn-default" disabled style={{ pointerEvents: 'none' }}>Update Sort Values</button>
                                                </span>
                                            </OverlayTrigger>
                                        }
                                        &nbsp;&nbsp;
                                    </span>
                                }
                                {this.state.surveyIsPublished === false &&
                                    <NavLink className="btn btn-primary" to={'/survey/' + this.props.match.params.id + '/questions/create'}>
                                        <span className="glyphicon glyphicon-plus" /> Create
                                    </NavLink>
                                }
                                {this.state.surveyIsPublished === true &&
                                    <OverlayTrigger animation={''} delay={0} placement="bottom" overlay={disabledTooltip}>
                                        <span style={{ cursor: 'not-allowed' }}>
                                            <button className="btn btn-primary" disabled style={{ pointerEvents: 'none' }}><span className="glyphicon glyphicon-plus" /> Create</button>
                                        </span>
                                    </OverlayTrigger>
                                }
                            </span>
                        </h3>
                        <i>These are all of the statements on this survey. Note that once a Survey has been published, you can no longer add/or subtract from the statement set. You can make minor content changes though.</i>
                        <div className="row">
                            <div className="col-sm-6">
                                <h4>True/False</h4>
                                <a className="btn btn-default" onClick={(e) => this.autoSortTrueFalse()}>Assign Auto Sort</a>
                                {this.StatementsDataGridTrueFalse()}
                            </div>
                            <div className="col-sm-6">
                                <h4>Likert</h4>
                                <a className="btn btn-default" onClick={(e) => this.autoSortLikert()}>Assign Auto Sort</a>
                                {this.StatementsDataGridLikert()}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-12">
                                <h4>Text Questions</h4>
                                {this.StatementsDataGridText()}
                            </div>
                        </div>

                        {this.state.originalEntity && this.state.originalEntity.questions.length > 0 && this.state.originalEntity.questions[0].previousQuestionID && <div>
                            <h3>Import</h3>
                            <div><i>Note that these will import any connected questions. Adding more questions to this survey will slowly make the data from imported surveys more and more incorrect.</i></div>
                            <div className="row">
                                <div className="col-xs-8">
                                    {this.props.FilterStore && <select className="form-control" onChange={(e: any) => this.setState({orgImportId: parseInt(e.target.value, 10)})}>
                                        {this.state.organizations && this.state.organizations.map((o: Organization) => {
                                            return <option key={`o-${o.id}`} value={o.id} selected={this.state.orgImportId === o.id}>{o.name}</option>;
                                        })}
                                    </select>}
                                </div>
                                <div className="col-xs-2">
                                    <a className="btn btn-primary" onClick={() => this.import()}>Import Survey Instances</a>
                                </div>
                            </div>
                        </div>}

                        <br />
                        <br />

                    </div>
                </div>
            </div>);
    }

    private sortQuestions(questionType: string) {
        if (this.state.originalEntity) {
            var entity = this.state.originalEntity;
            var questions = entity.questions;
            questions.filter((q: Question) => {
                return q.questionType === questionType;
            }).sort((q1: Question, q2: Question) => {
                if (q1.sort > q2.sort) {
                    return 1;
                } else if (q1.sort < q2.sort) {
                    return -1;
                }
                return 0;
            }).map((q: Question, ind: number) => {
                q.sort = ind + 1;
            });
            
            this.setState(
                {
                    originalEntity: Object.assign({}, this.state.originalEntity, { questions: questions }),
                    loading: true
                },
                () => {
                    this.updateQuestionSortOrder().then(() => {
                        
                        const id = this.props.match.params.id;

                        if (id) {
                            this.surveyDataService.get(id).then((survey: Survey) => {
                                survey.questions = survey.questions || [];
                                this.setState({ originalEntity: survey, loading: false });

                                if (survey.surveyVersions && survey.surveyVersions.some(x => x.published != null)) {
                                    this.setState({ surveyIsPublished: true });
                                } else {
                                    this.setState({ surveyIsPublished: false });
                                }
                            });
                        }
                            
                    });
                });
        }
    }

    private autoSortTrueFalse() {
        this.sortQuestions('binary');
    }

    private autoSortLikert() {
        this.sortQuestions('scale');
    }

    private import() {
        this.setState(
            {
                loading: true
            },
            () => {
                if (this.state.originalEntity) {
                    this.importDataService.Surveys(this.state.orgImportId, this.state.originalEntity.id).then(() => {
                        this.setState({
                            loading: false
                        });
                    });
                }
            });
    }

    private save(values: any) {
        this.surveyDataService.update(values).then((success) => {
            this.props.notificationService.addNotification('Survey updated successfully.');
        });
    }

    private createCopy() {
        this.setState({
            loading: true
        });
        this.surveyDataService.createCopy(this.state.originalEntity!.id).then((success) => {
            // quick fix for routing the user to the copied survey. 
            // you can't just do the second line because it won't trigger a rerender because of blocked updates in react
            this.props.history.push(`/survey`);
            this.props.history.push(`/survey/${success.id}`);
        });
    }

    private deleteQuestion(id: number) {
        const entity = this.state.originalEntity!.questions.find(e => e.id === id);

        var confirmed = confirm(`Are you sure you want to delete this statement? ${this.userLanguage ? Question.Text(entity!, this.userLanguage) : ''}`);

        if (confirmed) {
            this.questionDataService.delete(entity!).then(() => {
                var questions = this.state.originalEntity!.questions.filter(x => x.id !== id).sort((q1: Question, q2: Question) => {
                    if (q1.sort > q2.sort) { return 1; }
                    if (q1.sort < q2.sort) { return -1; }
                    return 0;
                });
                this.setState({
                    originalEntity: Object.assign({}, this.state.originalEntity, { questions: questions })
                });
                if (this.state.originalEntity) {
                    this.props.history.push('/');
                    this.props.history.push(`/survey/${this.state.originalEntity.id}`);
                }
            });
        }
    }

    private deleteQuestionBinder(id: number): ((event: React.MouseEvent<HTMLElement>) => void) {
        return this.deleteQuestion.bind(this, id);
    }
}


// WEBPACK FOOTER //
// ./src/components/survey/SurveyEdit.tsx